import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Filter, Route, Handshake, ArrowRight } from "lucide-react";
import { submitNetworkApplication, formError } from "@/lib/api";
import { SERVICES, BRAND } from "@/lib/content";
import { SectionHead } from "@/components/Sections";
import useSEO from "@/lib/useSEO";

const FEE_STYLES = ["Flat fees for defined work", "Hourly", "Both, depending on the matter"];

const GET = [
  { Icon: Filter, h: "Pre-screened matters", b: "Every request is reviewed by a licensed attorney first. You receive the issue, platforms, deadline and location already sorted." },
  { Icon: Route, h: "Only work in your lane", b: "Matters are routed by practice area and by the jurisdictions you're licensed in. Nothing you'd have to turn away." },
  { Icon: Handshake, h: "Clients who expect a quote", b: "Creators arrive knowing they'll get a written scope and price first, so the first call is about the work." },
];

const ASK = [
  "An active license in good standing, which we verify with your state bar",
  "Professional liability insurance, or telling the client in writing before they hire you that you don't carry it",
  "A reply to each match within one business day",
  "A written scope and fee before any work begins, with flat fees for defined work where you can",
  "A plain no when a matter isn't a fit, so we can re-route it quickly",
];

const initial = {
  name: "", email: "", phone: "", firm: "", bar_states: "", bar_numbers: "", years_licensed: "",
  practice_areas: [], fee_style: "", insured: "", profile_url: "", note: "", attest: false, fax: "",
};

export default function Attorneys() {
  useSEO({
    title: "For attorneys: join the Upload Counsel network",
    description: "Receive pre-screened creator-economy matters in your practice area and jurisdiction: contracts, IP, platform disputes, business formation and reputation.",
    canonical: "https://uploadlawyer.com/attorneys",
  });
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onInput = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const toggleArea = (t) =>
    setForm((f) => ({ ...f, practice_areas: f.practice_areas.includes(t) ? f.practice_areas.filter((x) => x !== t) : [...f.practice_areas, t] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email.trim())) return toast.error("Add your name and a working email.");
    if (!form.bar_states.trim()) return toast.error("List the states where you're licensed.");
    if (!form.practice_areas.length) return toast.error("Pick at least one practice area.");
    if (!form.attest) return toast.error("Please confirm the statement about your license.");
    setLoading(true);
    try {
      await submitNetworkApplication({ ...form, practice_areas: form.practice_areas.join(", ") });
      setDone(true);
      window.scrollTo({ top: document.getElementById("apply")?.offsetTop - 80 || 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formError(err, BRAND.email));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-attorneys">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 glow-tl" />
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-16 md:pt-24 md:pb-20">
          <p className="eyebrow">For attorneys</p>
          <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl text-[#F4F1EA] max-w-4xl leading-[1.02]">
            Creators need lawyers. <span className="italic text-[#D9B36C]">We send them to the right one.</span>
          </h1>
          <p className="mt-6 text-slate-300 text-lg sm:text-xl max-w-2xl leading-relaxed">
            Upload Counsel reviews creator legal requests and routes each one to a single vetted attorney who handles that work and is licensed where it needs handling. Apply to be one of them.
          </p>
          <a href="#apply" className="mt-9 inline-flex items-center gap-2 bg-[#D9B36C] text-[#0B0C10] font-semibold px-7 py-4 rounded-full hover:bg-[#E5C688] transition-colors">
            Apply to join <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <section className="relative py-20 md:py-24 border-t border-[#272B36]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHead eyebrow="What you get" title="Matters that are ready for a lawyer." />
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {GET.map(({ Icon, h, b }) => (
              <div key={h} className="rounded-2xl border border-[#272B36] bg-[#121419] p-7">
                <Icon size={20} className="text-[#D9B36C]" />
                <p className="mt-4 font-semibold text-[#F4F1EA] text-lg">{h}</p>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <SectionHead eyebrow="What we ask" title="Five commitments." body="The standards every creator is promised. They're the reason the referral is worth something." />
            </div>
            <ul className="lg:col-span-7 rounded-2xl border border-[#272B36] divide-y divide-[#272B36] bg-[#0F1116]">
              {ASK.map((a, i) => (
                <li key={a} className="flex gap-5 px-6 py-5">
                  <span className="font-display text-2xl text-[#D9B36C]/80 leading-none w-6 shrink-0">{i + 1}</span>
                  <span className="text-slate-300 leading-relaxed">{a}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="mt-10 text-sm text-slate-500 max-w-3xl leading-relaxed">
            Practice areas we route: {SERVICES.map((s) => s.title).join(", ")}. Any fee arrangement between attorneys follows the rules of
            professional conduct of the jurisdictions involved and is disclosed to the client in writing before engagement.
          </p>
        </div>
      </section>

      <section id="apply" className="relative py-20 md:py-24 border-t border-[#272B36] bg-[#0F1116] scroll-mt-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          {done ? (
            <div data-testid="network-success" className="rounded-3xl border border-[#D9B36C]/40 bg-[#121419] p-8 sm:p-12">
              <CheckCircle2 size={36} className="text-[#D9B36C]" />
              <h2 className="mt-5 font-display text-4xl text-[#F4F1EA]">Application received.</h2>
              <p className="mt-4 text-slate-300 leading-relaxed">
                We'll verify your license with your state bar and reply to <span className="text-[#F4F1EA]">{form.email}</span> about next steps.
              </p>
              <Link to="/" className="mt-8 inline-flex items-center gap-2 border border-[#272B36] text-[#F4F1EA] font-medium px-6 py-3 rounded-full hover:bg-white/5">Back to home</Link>
            </div>
          ) : (
            <>
              <SectionHead eyebrow="Apply" title="Join the network." body="Takes about three minutes. We verify every license before routing a matter." />
              <form onSubmit={submit} data-testid="network-form" className="mt-10 rounded-3xl border border-[#272B36] bg-[#121419] p-6 sm:p-8 md:p-10 space-y-5" noValidate>
                <input type="text" name="fax" value={form.fax} onChange={onInput("fax")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name" required><input data-testid="atty-name" value={form.name} onChange={onInput("name")} maxLength={200} className={inputCls} autoComplete="name" /></Field>
                  <Field label="Email" required><input data-testid="atty-email" type="email" value={form.email} onChange={onInput("email")} className={inputCls} autoComplete="email" /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Firm"><input data-testid="atty-firm" value={form.firm} onChange={onInput("firm")} maxLength={200} className={inputCls} autoComplete="organization" /></Field>
                  <Field label="Phone"><input data-testid="atty-phone" type="tel" value={form.phone} onChange={onInput("phone")} maxLength={40} className={inputCls} autoComplete="tel" /></Field>
                </div>
                <div className="grid sm:grid-cols-3 gap-5">
                  <div className="sm:col-span-2">
                    <Field label="States where you're licensed" required hint="e.g. Texas, California">
                      <input data-testid="atty-states" value={form.bar_states} onChange={onInput("bar_states")} maxLength={300} className={inputCls} />
                    </Field>
                  </div>
                  <Field label="Years licensed"><input data-testid="atty-years" inputMode="numeric" value={form.years_licensed} onChange={onInput("years_licensed")} maxLength={40} className={inputCls} /></Field>
                </div>
                <Field label="Bar number(s)" hint="Used only to verify your license with the state bar.">
                  <input data-testid="atty-bar" value={form.bar_numbers} onChange={onInput("bar_numbers")} maxLength={300} className={inputCls} />
                </Field>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Practice areas <span className="text-[#D9B36C]">*</span></p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {SERVICES.map((s) => {
                      const on = form.practice_areas.includes(s.title);
                      return (
                        <button type="button" key={s.slug} aria-pressed={on} onClick={() => toggleArea(s.title)}
                          className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${on ? "border-[#D9B36C] bg-[#D9B36C]/15 text-[#F4F1EA]" : "border-[#272B36] text-slate-300 hover:border-[#D9B36C]/40"}`}>
                          {s.title}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="How you usually bill">
                    <select data-testid="atty-fees" value={form.fee_style} onChange={onInput("fee_style")} className={inputCls}>
                      <option value="">Select…</option>
                      {FEE_STYLES.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </Field>
                  <Field label="Professional liability insurance">
                    <select data-testid="atty-insured" value={form.insured} onChange={onInput("insured")} className={inputCls}>
                      <option value="">Select…</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </Field>
                </div>
                <Field label="Website or LinkedIn"><input data-testid="atty-url" value={form.profile_url} onChange={onInput("profile_url")} maxLength={300} className={inputCls} placeholder="https://" /></Field>
                <Field label="Anything else" hint="Creator clients you've represented, platforms you know well, languages.">
                  <textarea data-testid="atty-note" rows={4} value={form.note} onChange={onInput("note")} maxLength={4000} className={`${inputCls} resize-y`} />
                </Field>
                <label className="flex items-start gap-3 text-sm text-slate-400 cursor-pointer leading-relaxed">
                  <input type="checkbox" data-testid="atty-attest" checked={form.attest} onChange={onInput("attest")} className="mt-1 h-4 w-4 shrink-0 accent-[#D9B36C]" />
                  <span>I hold an active license in the states listed and am in good standing. I understand Upload Counsel will verify this with the state bar before routing any matter, and that joining does not guarantee referrals.</span>
                </label>
                <div className="pt-2">
                  <button type="submit" disabled={loading} data-testid="atty-submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#D9B36C] text-[#0B0C10] font-semibold px-8 py-3.5 rounded-full hover:bg-[#E5C688] transition-colors disabled:opacity-50">
                    {loading ? "Sending…" : "Send application"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-[#0F1116] border border-[#272B36] rounded-xl px-4 py-3 text-[#F4F1EA] text-base sm:text-sm placeholder:text-slate-600 outline-none focus:border-[#D9B36C] focus:ring-1 focus:ring-[#D9B36C] transition-colors";

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold">
        {label} {required && <span className="text-[#D9B36C]">*</span>}
      </span>
      <div className="mt-2">{children}</div>
      {hint && <span className="block mt-1.5 text-xs text-slate-500">{hint}</span>}
    </label>
  );
}
